import {
  useDeleteCategoriesCategoryId,
  useGetCategories,
  usePatchCategoriesCategoryId,
  usePostCategories,
  getGetCategoriesQueryKey,
} from '@agos/api-client/menu'
import type { CategoryWithTranslations } from '@agos/api-client/model'
import { CATEGORY_PALETTE, T, categoryColour, tt, useModalFocus } from '@agos/shared'
import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  cn,
  toast,
  type Column,
  BUTTON_PRIMARY,
  CATEGORY_ICONS,
  CategoryIcon,
} from '@agos/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import {
  ClipboardList,
  Edit2,
  Eye,
  EyeOff,
  Image,
  ImageOff,
  Plus,
  Tags,
  Trash2,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

/**
 * Ported from legacy `pages/categories.tsx`. Spec: `docs/pages/manager-menu/categories.md`.
 *
 * No polling: categories change a few times a year, so the page fetches on
 * mount and invalidates on write, exactly as legacy does.
 */

const PAGE_SIZE = 20
/** Legacy spaces new categories ten apart so one can be slipped between two. */
const SORT_ORDER_STEP = 10

const categoryFormSchema = z.object({
  name: z.string().min(2, tt('Meno je povinné')),
  description: z.string().optional(),
  sortOrder: z.coerce.number().default(0),
  visibleOnWeb: z.boolean().default(true),
  hideImage: z.boolean().default(false),
  containsFood: z.boolean().default(false),
  // §9.15. `''` is *no colour*, which is what the backend stores as null — so clearing it here and
  // never choosing one send the same thing.
  color: z.string().default(''),
  // §9.72. Same convention as the colour: `''` is *no icon*, which the backend stores as null.
  icon: z.string().default(''),
})

/**
 * `sortOrder` arrives from a number input as a string, so the schema coerces it
 * — which makes the form's input and output types genuinely different. Naming
 * both is what keeps react-hook-form's resolver typed rather than widened.
 */
type CategoryFormInput = z.input<typeof categoryFormSchema>
type CategoryFormValues = z.output<typeof categoryFormSchema>

const EMPTY_FORM: CategoryFormInput = {
  name: '',
  description: '',
  sortOrder: 0,
  visibleOnWeb: true,
  hideImage: false,
  containsFood: false,
  color: '',
  icon: '',
}

const FIELD_CLASSES =
  'w-full px-4 py-3 rounded-xl bg-background border border-border focus:ring-2 focus:ring-primary/20 outline-none'
const CHECKBOX_ROW_CLASSES =
  'flex items-center gap-3 cursor-pointer p-4 border border-border rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors'

/**
 * Emoji offered beside the free field — §9.72.
 *
 * **Suggestions, not a whitelist.** The owner chose a free emoji over a curated set, so these are the
 * ones a restaurant reaches for first and nothing refuses anything else. Food before the rest,
 * because a menu's categories are mostly food.
 */
// Emoji are the value here, not translatable text — the rule that would translate them does not apply.
// eslint-disable-next-line no-restricted-syntax
// §9.91 — the daily menu's own vocabulary, replacing ten emoji at the owner's word: *"remove
// emojis, keep the one from obedové menu and replace them with these"*. The names are what the
// column stores. `CategoryIcon` draws them, and where the column still holds an emoji it draws the
// glyph the **category's own name** asks for — so `☕ Polievky` reads as a bowl of soup without this
// screen having written anything. An emoji no name can explain is still printed as it stands.
const CATEGORY_ICON_SUGGESTIONS = Object.keys(CATEGORY_ICONS)

export function CategoriesPage() {
  const queryClient = useQueryClient()
  const { data: categories } = useGetCategories()

  const createCategory = usePostCategories()
  const updateCategory = usePatchCategoriesCategoryId()
  const deleteCategory = useDeleteCategoriesCategoryId()

  const [search, setSearch] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [pendingDeletion, setPendingDeletion] = useState<CategoryWithTranslations | null>(null)

  const form = useForm<CategoryFormInput, unknown, CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: EMPTY_FORM,
  })

  const refreshCategories = () =>
    queryClient.invalidateQueries({ queryKey: getGetCategoriesQueryKey() })

  const openForNew = () => {
    setEditingId(null)
    form.reset({ ...EMPTY_FORM, sortOrder: (categories?.length ?? 0) * SORT_ORDER_STEP })
    setIsDialogOpen(true)
  }

  const openForEdit = (category: CategoryWithTranslations) => {
    setEditingId(category.id ?? null)
    form.reset({
      name: category.name ?? '',
      description: category.description ?? '',
      sortOrder: category.sortOrder ?? 0,
      visibleOnWeb: category.visibleOnWeb ?? true,
      hideImage: category.hideImage ?? false,
      containsFood: category.containsFood ?? false,
      color: categoryColour(category.color) ?? '',
      // §9.72 — free text, so it is loaded as it was stored rather than through a validator.
      icon: category.icon ?? '',
    })
    setIsDialogOpen(true)
  }

  const submitForm = (values: CategoryFormValues) => {
    if (editingId !== null) {
      updateCategory.mutate(
        { categoryId: editingId, data: values },
        {
          onSuccess: () => {
            void refreshCategories()
            setIsDialogOpen(false)
            toast({ title: tt('Uložené'), description: tt('Kategória bola upravená.') })
          },
        },
      )
      return
    }

    createCategory.mutate(
      { data: values },
      {
        onSuccess: () => {
          void refreshCategories()
          setIsDialogOpen(false)
          toast({ title: tt('Úspech'), description: tt('Kategória bola pridaná.') })
        },
      },
    )
  }

  const confirmDeletion = () => {
    const categoryId = pendingDeletion?.id
    if (categoryId === undefined) return

    deleteCategory.mutate(
      { categoryId },
      {
        onSuccess: () => {
          void refreshCategories()
          setPendingDeletion(null)
          toast({ title: tt('Vymazané'), description: tt('Kategória bola odstránená.') })
        },
      },
    )
  }

  const closeDialog = () => setIsDialogOpen(false)

  /* §9.129 gave this dialog Escape because the owner reported one that *"nedá sa zatvoriť"*. §9.495 moves it
     onto the focus hook and **deletes the `window` listener rather than leaving it beside one**: §9.480's
     regression was exactly two mechanisms for one behaviour - `ConfirmDialog` answered Escape through a
     `document` listener, so a confirmation opened over another overlay closed both. The hook's Escape is
     element-level, which is the stronger version of the fix the owner asked for. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(isDialogOpen, panelRef, closeDialog)

  /** The three column toggles are one mutation each, with their own wording. */
  const toggleFlag = (
    category: CategoryWithTranslations,
    field: 'visibleOnWeb' | 'hideImage' | 'containsFood' | 'visibleInManager',
    titles: { on: string; off: string },
    describe: (turnedOn: boolean) => string,
  ) => {
    const categoryId = category.id
    if (categoryId === undefined) return
    const turnedOn = !category[field]

    updateCategory.mutate(
      { categoryId, data: { [field]: turnedOn } },
      {
        onSuccess: () => {
          void refreshCategories()
          toast({ title: turnedOn ? titles.on : titles.off, description: describe(turnedOn) })
        },
      },
    )
  }

  const visibleCategories = useMemo(() => {
    const inDisplayOrder = [...(categories ?? [])].sort(
      (left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0),
    )
    const query = search.trim().toLowerCase()
    if (!query) return inDisplayOrder

    return inDisplayOrder.filter(
      (category) =>
        category.name?.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query),
    )
  }, [categories, search])

  const columns: Column<CategoryWithTranslations>[] = [
    {
      key: 'sortOrder',
      header: '#',
      headerClassName: 'text-center w-16',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => row.sortOrder ?? 0,
      render: (row) => (
        <span className="font-mono text-muted-foreground">{row.sortOrder ?? 0}</span>
      ),
    },
    {
      key: 'name',
      header: tt('Názov kategórie'),
      sortable: true,
      sortValue: (row) => row.name ?? '',
      render: (row) => {
        // §9.27 — *"v tabulke pri názve kategórií pridať bodku ... kde sa ukáže aktuálna farba"*. The
        // colour was choosable and then invisible everywhere but the order builder, so the only way
        // to see what a category had been given was to open its form.
        const dot = categoryColour(row.color)
        return (
          <div>
            <div
              className={cn(
                'font-bold text-base flex items-center gap-2',
                !row.visibleOnWeb && 'text-muted-foreground',
              )}
            >
              {dot !== null && (
                <span
                  data-category-colour-dot={dot}
                  // Solid, unlike the builder's chip: a 4-pixel dot tinted to 10% is a grey smudge,
                  // and this one is not sitting beside a selection highlight it has to lose to.
                  style={{ backgroundColor: dot }}
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  aria-hidden="true"
                />
              )}
              <T>{row.name}</T>
            </div>
            {row.description && (
              <div className="text-xs text-muted-foreground mt-1">
                <T>{row.description}</T>
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'visibleOnWeb',
      header: tt('Viditeľnosť na webe'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.visibleOnWeb ? 1 : 0),
      render: (row) => (
        <button
          type="button"
          title={row.visibleOnWeb ? tt('Skryť z webu') : tt('Zobraziť na webe')}
          onClick={(event) => {
            event.stopPropagation()
            toggleFlag(
              row,
              'visibleOnWeb',
              { on: tt('Zobrazená na webe'), off: tt('Skrytá z webu') },
              (turnedOn) =>
                `${tt('Kategória')} "${row.name ?? ''}" ${
                  turnedOn ? tt('je teraz viditeľná') : tt('nie je zobrazená')
                } ${tt('na zákazníckej stránke.')}`,
            )
          }}
          className="inline-flex"
        >
          <StatusBadge
            label={row.visibleOnWeb ? tt('Zobrazená') : tt('Skrytá')}
            color={row.visibleOnWeb ? 'green' : 'zinc'}
            icon={row.visibleOnWeb ? Eye : EyeOff}
            size="md"
          />
        </button>
      ),
    },
    {
      key: 'hideImage',
      header: tt('Obrázky'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.hideImage ? 1 : 0),
      render: (row) => (
        <button
          type="button"
          title={row.hideImage ? tt('Zobraziť obrázky') : tt('Skryť obrázky')}
          onClick={(event) => {
            event.stopPropagation()
            toggleFlag(
              row,
              'hideImage',
              { on: tt('Obrázky skryté'), off: tt('Obrázky zobrazené') },
              (turnedOn) =>
                `${tt('Kategória')} "${row.name ?? ''}": ${tt('obrázky sú')} ${
                  turnedOn ? tt('skryté') : tt('zobrazené')
                }.`,
            )
          }}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all',
            row.hideImage
              ? 'bg-orange-500/10 text-orange-600 border border-orange-500/30 hover:bg-orange-500/20'
              : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/20',
          )}
        >
          {row.hideImage ? <ImageOff className="w-3.5 h-3.5" /> : <Image className="w-3.5 h-3.5" />}
          {row.hideImage ? tt('Skryté') : tt('Zobrazené')}
        </button>
      ),
    },
    {
      key: 'containsFood',
      header: tt('Kuchyňa'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      sortValue: (row) => (row.containsFood ? 1 : 0),
      render: (row) => (
        <button
          type="button"
          title={
            row.containsFood ? tt('Vypnúť zobrazenie v kuchyni') : tt('Zapnúť zobrazenie v kuchyni')
          }
          onClick={(event) => {
            event.stopPropagation()
            toggleFlag(
              row,
              'containsFood',
              { on: tt('Kategória obsahuje jedlo'), off: tt('Kategória neobsahuje jedlo') },
              (turnedOn) =>
                `${tt('Kategória')} "${row.name ?? ''}" ${
                  turnedOn ? tt('sa zobrazí') : tt('sa nebude zobrazovať')
                } ${tt('na kuchynskom displeji.')}`,
            )
          }}
          className="inline-flex"
        >
          <StatusBadge
            label={row.containsFood ? tt('Jedlo') : tt('Nápoje')}
            color={row.containsFood ? 'green' : 'blue'}
            icon={UtensilsCrossed}
            size="md"
          />
        </button>
      ),
    },
    {
      // §9.189 — the owner's *Grátis Veci* case: a category that exists only to be drawn from by a
      // kombo box has no business in the row a waiter scrolls. `visibleOnWeb` was the only way to
      // remove it and that changes what the *customer* sees.
      key: 'visibleInManager',
      header: tt('Nová objednávka'),
      headerClassName: 'text-center',
      className: 'text-center',
      sortable: true,
      // §9.155's rule — `!== false`, so a payload from before the column reads as visible and no
      // franchise loses a category by upgrading.
      sortValue: (row) => (row.visibleInManager === false ? 0 : 1),
      render: (row) => (
        <button
          type="button"
          data-visible-in-manager={row.id}
          title={
            row.visibleInManager === false
              ? tt('Zapnúť v novej objednávke')
              : tt('Skryť v novej objednávke')
          }
          onClick={(event) => {
            event.stopPropagation()
            toggleFlag(
              row,
              'visibleInManager',
              {
                on: tt('Kategória je v novej objednávke'),
                off: tt('Kategória je skrytá v novej objednávke'),
              },
              (turnedOn) =>
                `${tt('Kategória')} "${row.name ?? ''}" ${
                  turnedOn ? tt('sa zobrazí') : tt('sa nebude zobrazovať')
                } ${tt('pri zadávaní objednávky. Na zákaznícku stránku to nemá vplyv.')}`,
            )
          }}
          className="inline-flex"
        >
          <StatusBadge
            label={row.visibleInManager === false ? tt('Skrytá') : tt('Zobrazená')}
            color={row.visibleInManager === false ? 'zinc' : 'green'}
            icon={ClipboardList}
            size="md"
          />
        </button>
      ),
    },
    {
      key: 'actions',
      header: tt('Akcie'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            aria-label={tt('Upraviť')}
            onClick={(event) => {
              event.stopPropagation()
              openForEdit(row)
            }}
            className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label={tt('Zmazať')}
            onClick={(event) => {
              event.stopPropagation()
              setPendingDeletion(row)
            }}
            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]

  const isSaving = createCategory.isPending || updateCategory.isPending

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Kategórie produktov')}
        description={tt('Spravujte rozdelenie produktov do kategórií a ich viditeľnosť na webe')}
        icon={<Tags className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={openForNew}
            // §9.3 — these constants are colour and weight only; the *box* belongs at the call
            // site, and this one had never supplied it. So the header's action rendered as a bare
            // rectangle while every other page-header button on the manager is a padded pill.
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm',
              BUTTON_PRIMARY,
            )}
          >
            <Plus className="w-5 h-5" />
            <T>Pridať kategóriu</T>
          </button>
        }
      />

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať kategóriu...') }}
      />

      <DataTable
        columns={columns}
        data={visibleCategories}
        rowKey={(row) => row.id ?? row.name ?? ''}
        pageSize={PAGE_SIZE}
        emptyMessage={tt('Žiadne kategórie nenájdené.')}
      />

      {/* FB-011: legacy asks with the browser's native confirm(), which on a POS
          tablet is a system dialog outside the app's reach. Same wording - and
          the warning that products move to "Nezaradené" rather than vanish. */}
      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={confirmDeletion}
        loading={deleteCategory.isPending}
        variant="danger"
        title={tt('Vymazať kategóriu?')}
        description={tt(
          'Naozaj chcete vymazať túto kategóriu? Týmto sa produkty presunú do Nezaradené.',
        )}
        confirmLabel={tt('Vymazať')}
      />

      {isDialogOpen && (
        // §9.129 — the overlay closes on a click and the card stops it propagating, which is the
        // pattern every other dialog in the manager already uses.
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editingId !== null ? tt('Upraviť kategóriu') : tt('Nová kategória')}
          onClick={closeDialog}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          {/* `max-h` and a scrolling body are the whole of the owner's *"nedá sa zatvoriť"*: the form
            * is taller than a laptop screen, centring pushed the header off the top and the footer
            * off the bottom, and `overflow-hidden` meant neither could be scrolled back. */}
          <div
            onClick={(event) => event.stopPropagation()}
            ref={panelRef}
            onKeyDown={onKeyDown}
            data-category-dialog
            className="bg-card rounded-3xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-border"
          >
            <div className="px-6 py-5 border-b border-border flex justify-between items-center bg-muted/30 shrink-0">
              <h2 className="text-xl font-bold font-display">
                {editingId !== null ? tt('Upraviť kategóriu') : tt('Nová kategória')}
              </h2>
              <button
                type="button"
                aria-label={tt('Zatvoriť')}
                data-dialog-cancel
                onClick={closeDialog}
                className="p-2 hover:bg-background rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form
                id="categoryForm"
                onSubmit={(event) => void form.handleSubmit(submitForm)(event)}
                className="space-y-5"
              >
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground" htmlFor="category-nazov-kategorie">
                    <T>Názov kategórie</T>
                  </label>
                  <input id="category-nazov-kategorie" {...form.register('name')} className={FIELD_CLASSES} />
                  {form.formState.errors.name && (
                    <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground" htmlFor="category-popis-volitelne">
                    <T>Popis (voliteľné)</T>
                  </label>
                  <textarea
                    id="category-popis-volitelne"
                    {...form.register('description')}
                    rows={2}
                    className={cn(FIELD_CLASSES, 'resize-none')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground" htmlFor="category-poradie-zobrazenia">
                    <T>Poradie zobrazenia</T>
                  </label>
                  <input id="category-poradie-zobrazenia" type="number" {...form.register('sortOrder')} className={FIELD_CLASSES} />
                  <p className="text-xs text-muted-foreground">
                    <T>Menšie číslo sa zobrazí skôr (napr. 10, 20, 30)</T>
                  </p>
                </div>

                <label className={CHECKBOX_ROW_CLASSES}>
                  <input
                    type="checkbox"
                    {...form.register('visibleOnWeb')}
                    className="w-5 h-5 rounded accent-primary"
                  />
                  <div>
                    <span className="font-semibold flex items-center gap-1.5">
                      <Eye className="w-4 h-4 text-primary" />{' '}
                      <T>Viditeľná na zákazníckej stránke</T>
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <T>Zákazníci uvidia túto kategóriu pri objednávaní online</T>
                    </p>
                  </div>
                </label>

                <label className={CHECKBOX_ROW_CLASSES}>
                  <input
                    type="checkbox"
                    {...form.register('hideImage')}
                    className="w-5 h-5 rounded accent-primary"
                  />
                  <div>
                    <span className="font-semibold flex items-center gap-1.5">
                      <ImageOff className="w-4 h-4 text-orange-500" />{' '}
                      <T>Skryť obrázky produktov</T>
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <T>
                        Produkty v tejto kategórii sa zobrazia bez obrázku (napr. dresingy, nápoje)
                      </T>
                    </p>
                  </div>
                </label>

                <label className={CHECKBOX_ROW_CLASSES}>
                  <input
                    type="checkbox"
                    {...form.register('containsFood')}
                    className="w-5 h-5 rounded accent-primary"
                  />
                  <div>
                    <span className="font-semibold flex items-center gap-1.5">
                      <UtensilsCrossed className="w-4 h-4 text-amber-500" />{' '}
                      <T>Táto kategória obsahuje jedlo</T>
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <T>Produkty z tejto kategórie sa zobrazia na kuchynskom displeji</T>
                    </p>
                  </div>
                </label>

                {/* §9.72, owner decision 2026-08-06 - an emoji beside the name. A free field, not a
                  * curated set: that was one of the three offered and it is the one not chosen. The
                  * palette is suggestions, exactly as the colour's swatches are beside its wheel, and
                  * the tier editor already works this way. */}
                <div className="space-y-2">
                  <span className="font-semibold text-sm">
                    <T>Ikona kategórie</T>
                  </span>
                  <p className="text-xs text-muted-foreground">
                    <T>Zobrazí sa pri názve kategórie v menu. Nechajte prázdne pre žiadnu.</T>
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      data-category-icon
                      value={form.watch('icon') ?? ''}
                      onChange={(event) =>
                        form.setValue('icon', event.target.value, { shouldDirty: true })
                      }
                      placeholder={tt('napr. pizza')}
                      maxLength={32}
                      aria-label={tt('Ikona kategórie')}
                      className="w-24 min-h-[40px] rounded-xl border border-border bg-background px-3 text-lg text-center"
                    />
                    <div className="flex flex-wrap gap-1" data-icon-palette>
                      {CATEGORY_ICON_SUGGESTIONS.map((name) => (
                        <button
                          key={name}
                          type="button"
                          data-icon={name}
                          aria-label={name}
                          onClick={() => form.setValue('icon', name, { shouldDirty: true })}
                          className="w-9 h-9 rounded-xl border border-border hover:bg-muted flex items-center justify-center"
                        >
                          <CategoryIcon icon={name} className="w-4 h-4" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* §9.15. Twelve colours and a *none*, not a colour input: free hex would let somebody
                 * pick a shade the label on top cannot be read against, on a screen used at speed. */}
                <div className="space-y-2">
                  <span className="font-semibold text-sm">
                    <T>Farba kategórie</T>
                  </span>
                  <p className="text-xs text-muted-foreground">
                    <T>Zobrazí sa pri výbere kategórie v novej objednávke</T>
                  </p>
                  <div className="flex flex-wrap gap-2" data-colour-picker>
                    <button
                      type="button"
                      data-colour=""
                      aria-label={tt('Bez farby')}
                      aria-pressed={form.watch('color') === ''}
                      onClick={() => form.setValue('color', '', { shouldDirty: true })}
                      className={cn(
                        'px-3 py-2 rounded-xl border text-xs font-semibold transition-all',
                        form.watch('color') === ''
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      <T>Bez farby</T>
                    </button>
                    {CATEGORY_PALETTE.map((swatch) => (
                      <button
                        key={swatch}
                        type="button"
                        data-colour={swatch}
                        aria-label={swatch}
                        aria-pressed={form.watch('color') === swatch}
                        onClick={() => form.setValue('color', swatch, { shouldDirty: true })}
                        style={{ backgroundColor: swatch }}
                        className={cn(
                          'w-9 h-9 rounded-xl border-2 transition-all',
                          form.watch('color') === swatch
                            ? 'border-foreground scale-110'
                            : 'border-transparent hover:scale-105',
                        )}
                      />
                    ))}
                  </div>
                  {/* §9.27, owner decision 2026-08-05 - any colour, not only the twelve. The swatches
                   * stay because picking one is faster than a wheel when *any green will do*; the
                   * wheel is for when it is not. `readableInk` is what keeps the label legible, so
                   * there is no shade that has to be kept out. */}
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="color"
                      value={
                        /^#[0-9a-f]{6}$/i.test(form.watch('color') ?? '')
                          ? (form.watch('color') ?? '#3b82f6')
                          : '#3b82f6'
                      }
                      onChange={(event) =>
                        form.setValue('color', event.target.value, { shouldDirty: true })
                      }
                      aria-label={tt('Vlastná farba')}
                      data-colour-custom
                      className="w-8 h-8 rounded-lg border border-border bg-card p-0.5 cursor-pointer"
                    />
                    <T>Vlastná farba</T>
                  </label>{' '}
                </div>
              </form>
            </div>

            <div className="px-6 py-4 border-t border-border bg-muted/30 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeDialog}
                className="px-5 py-2.5 rounded-xl font-bold text-muted-foreground hover:bg-background transition-colors"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="submit"
                form="categoryForm"
                disabled={isSaving}
                className={cn('px-6 py-2.5 rounded-xl font-bold', BUTTON_PRIMARY)}
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
